import * as React from 'react';
import { getAuthToken } from '../lib/apiClient';

/**
 * Fetches an authenticated image URL and returns a local object URL suitable
 * for use in <img src=...>. Handles token-protected asset endpoints.
 *
 * The object URL is revoked on unmount or when `url` changes to prevent leaks.
 * Returns null while loading or if no url is provided.
 */
export function useProtectedImageUrl(url: string | null | undefined): string | null {
  const [objectUrl, setObjectUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!url) {
      setObjectUrl(null);
      return;
    }

    const state = { cancelled: false, blobUrl: null as string | null };

    void (async () => {
      try {
        const token = await getAuthToken();
        if (state.cancelled) return;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok || state.cancelled) return;
        const blob = await res.blob();
        if (state.cancelled) return;
        state.blobUrl = URL.createObjectURL(blob);
        setObjectUrl(state.blobUrl);
      } catch {
        // ignore fetch/auth failures - logo is best-effort
      }
    })();

    return () => {
      state.cancelled = true;
      if (state.blobUrl) {
        URL.revokeObjectURL(state.blobUrl);
        state.blobUrl = null;
      }
    };
  }, [url]);

  return objectUrl;
}
